import { readableReason } from "@/helpers/humanText";
import axios from "@/plugins/axios";

const ACCOUNTS_URL = "/intake/admin/brokerage-accounts";

const ADD_FAILED = "The address could not be added. Try again in a moment.";
const UPDATE_FAILED = "The address could not be updated. Try again in a moment.";
// The row was removed under the page: the page reads the list again, so say so plainly.
const ADDRESS_GONE = "That address no longer exists. The list has been refreshed.";

// Only the API's own short plain sentence is shown. Server errors (5xx), a body that is not
// JSON, gateway text ("upstream ...") and long text get the caller's fixed sentence instead.
const ownSentence = (message, fallback) => {
  if (typeof message !== "string" || message.length > 200 || /upstream/i.test(message)) return fallback;
  return readableReason(message, fallback);
};

// The API's message for a failed call, or a fallback.
const apiMessage = (error, fallback) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status >= 500 || !data || typeof data !== "object") return fallback;
  return ownSentence(data.message, fallback);
};

// Staff-only intake: the health page and the brokerage intake addresses (slugs).
export default {
  namespaced: true,

  state: () => ({
    health: null,
    loadingHealth: false,
    // ISO time of the last successful load.
    checkedAt: null,
    accounts: [],
    loadingAccounts: false,
    // Broker and wholesale organizations for the add dialog: [{ entity_id, name, website }].
    brokerages: [],
  }),

  mutations: {
    SET_HEALTH(state, health) {
      state.health = health;
    },

    SET_LOADING_HEALTH(state, loading) {
      state.loadingHealth = loading;
    },

    SET_CHECKED_AT(state, checkedAt) {
      state.checkedAt = checkedAt;
    },

    SET_ACCOUNTS(state, accounts) {
      state.accounts = accounts;
    },

    SET_LOADING_ACCOUNTS(state, loading) {
      state.loadingAccounts = loading;
    },

    SET_BROKERAGES(state, brokerages) {
      state.brokerages = brokerages;
    },

    UPSERT_ACCOUNT(state, account) {
      const index = state.accounts.findIndex((a) => a.entity_id === account.entity_id);
      if (index === -1) {
        state.accounts = [...state.accounts, account].sort((a, b) => a.slug.localeCompare(b.slug));
      } else {
        state.accounts.splice(index, 1, account);
      }
    },
  },

  actions: {
    async fetchHealth({ commit }) {
      commit("SET_LOADING_HEALTH", true);

      try {
        const response = await axios.get("/intake/admin/health");
        const health = {
          // No mail block at all: nothing is known, so the last email received is "Unknown".
          mail: response.data.mail || {
            webhook_configured: false,
            last_received_at: null,
            last_received_readable: false,
          },
          // Slug guard: null when the API could not read them.
          slugGuard: {
            conflicts: Array.isArray(response.data.reserved_slug_conflicts)
              ? response.data.reserved_slug_conflicts
              : null,
            carrierSlugsKnown:
              typeof response.data.carrier_intake_slugs_known === "number"
                ? response.data.carrier_intake_slugs_known
                : null,
          },
        };
        commit("SET_HEALTH", health);
        commit("SET_CHECKED_AT", new Date().toISOString());
        return health;
      } catch (error) {
        commit("SET_HEALTH", null);
        throw error;
      } finally {
        commit("SET_LOADING_HEALTH", false);
      }
    },

    async fetchAccounts({ commit }) {
      commit("SET_LOADING_ACCOUNTS", true);
      try {
        const response = await axios.get(ACCOUNTS_URL);
        const accounts = response.data.accounts || [];
        commit("SET_ACCOUNTS", accounts);
        return accounts;
      } finally {
        commit("SET_LOADING_ACCOUNTS", false);
      }
    },

    async fetchBrokerages({ commit }) {
      const response = await axios.get("/admin/brokerages/dropdown", { params: { active_only: "true" } });
      const brokerages = (response.data.brokerages || [])
        .map((b) => ({ entity_id: b.entity_id, name: b.name || "", website: b.website || "" }))
        .sort((a, b) => a.name.localeCompare(b.name));
      commit("SET_BROKERAGES", brokerages);
      return brokerages;
    },

    // The add dialog shows the API's error itself, so the global toast is off.
    // Throws an Error whose message is the API's message.
    async createAccount({ commit }, { slug, organizationId, displayName }) {
      let response;
      try {
        response = await axios.post(
          ACCOUNTS_URL,
          { slug, organization_id: organizationId, display_name: displayName || "" },
          { withoutErrorHandler: true }
        );
      } catch (error) {
        throw new Error(apiMessage(error, ADD_FAILED));
      }
      if (!response.data?.success) {
        throw new Error(ownSentence(response.data?.message, ADD_FAILED));
      }
      commit("UPSERT_ACCOUNT", response.data.account);
      return response.data.account;
    },

    // The global toast would say only "Something went wrong" for a 409, so this
    // shows the API's own message instead (e.g. the brokerage already has an
    // active address).
    async setAccountStatus({ commit, dispatch }, { id, status }) {
      let response;
      let message = "";
      try {
        response = await axios.post(`${ACCOUNTS_URL}/${id}`, { status }, { withoutErrorHandler: true });
        if (!response.data?.success) message = ownSentence(response.data?.message, UPDATE_FAILED);
      } catch (error) {
        message =
          error.response?.status === 404 ? ADDRESS_GONE : apiMessage(error, UPDATE_FAILED);
      }
      if (message) {
        dispatch("showMessage", { message, messageType: "error", messageTitle: "Error" }, { root: true });
        throw new Error(message);
      }
      commit("UPSERT_ACCOUNT", response.data.account);
      return response.data.account;
    },
  },
};
